/**
 * JumpToChip
 *
 * Always-visible header affordance that surfaces the ⌘K palette.
 * Renders as a 32×32 subtle icon button that visually matches the
 * neighboring header actions (feedback, docs, notifications). The
 * keyboard shortcut is surfaced via the tooltip on hover/focus rather
 * than an inline keycap, keeping the header dense and consistent.
 *
 * - Renders in every route, including the workflow designer. Each route
 *   binds ⌘K to its own palette (global vs designer-local); the button
 *   synthesizes the same keystroke so whichever handler is active
 *   responds.
 *
 * A11y: `aria-keyshortcuts="Meta+K Control+K"` so screen readers announce
 * the cross-platform shortcut. The tooltip carries the visible shortcut
 * hint for sighted users.
 */

import { Button, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { ariaKeyShortcut, formatShortcut, IS_MAC } from '@/utils/input';

const useStyles = makeStyles({
  // Match the sizing of neighboring header icon buttons (feedback, docs,
  // notifications) so the row reads as a uniform action cluster. The
  // parent nav flex container owns the horizontal gap between siblings.
  button: {
    minWidth: '32px',
    height: '32px',
    paddingInline: tokens.spacingHorizontalXS,
  },
});

export const JumpToChip = () => {
  const styles = useStyles();

  // Synthesize a ⌘K / Ctrl+K keydown so whichever palette is bound for
  // the current route (global outside designer, designer's local inside)
  // responds with its own toggle. This keeps the button route-agnostic
  // without coupling it to either palette's state.
  const triggerPalette = () => {
    const event = new KeyboardEvent('keydown', {
      key: 'k',
      code: 'KeyK',
      metaKey: IS_MAC,
      ctrlKey: !IS_MAC,
      bubbles: true,
      cancelable: true,
    });
    document.dispatchEvent(event);
  };

  const tooltipLabel = `Jump to anything (${formatShortcut('K')})`;

  return (
    <Tooltip content={tooltipLabel} relationship="description">
      <Button
        appearance="subtle"
        icon={<SearchRegular />}
        className={styles.button}
        onClick={triggerPalette}
        aria-label="Jump to anything"
        aria-keyshortcuts={ariaKeyShortcut('K')}
      />
    </Tooltip>
  );
};
